import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, Check, Minus, Search, X, AlertCircle } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Texto secundario tenue a la izquierda (ej. el código "0001") */
  code?: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  /** Etiqueta del campo */
  label?: string;
  /** Contenido adicional en el label (tooltips, badges, etc.) */
  labelAction?: ReactNode;
  /** Si el campo es requerido */
  required?: boolean;
  /** Mensaje de error a mostrar */
  error?: string;
  /** Texto de ayuda debajo del campo */
  helperText?: string;
  /** Opciones disponibles */
  options?: MultiSelectOption[];
  /** Valores seleccionados (controlado) */
  value: string[];
  /** Callback al cambiar la selección */
  onChange: (values: string[]) => void;
  /** Placeholder cuando no hay nada seleccionado */
  placeholder?: string;
  /** Etiqueta del item maestro "seleccionar todo" */
  selectAllLabel?: string;
  /** Texto que se muestra en el trigger cuando están todos seleccionados */
  allSelectedLabel?: string;
  /** Tamaño del campo */
  /** `auto`: 32 px por debajo de 1536 px y 40 px desde ahí. */
  size?: 'small' | 'medium' | 'large' | 'auto';
  /** Nº de chips antes de colapsar a "+N" */
  maxChips?: number;
  /**
   * Muestra una "×" en el trigger para vaciar la selección.
   * Mantener APAGADO en campos requeridos (ej. Disponibilidad): limpiar
   * dejaría el campo en estado inválido.
   */
  clearable?: boolean;
  /** Nº de opciones a partir del cual se muestra el buscador */
  searchThreshold?: number;
  /** Nº de opciones a partir del cual el panel muestra el footer con "Limpiar" */
  clearThreshold?: number;
  /** Clases adicionales para el contenedor */
  containerClassName?: string;
  /** ID único para accesibilidad */
  id?: string;
  /** Nombre semántico del control */
  name?: string;
  /** Deshabilitado */
  disabled?: boolean;
}

/**
 * Componente MultiSelect reutilizable
 *
 * Selección múltiple con apariencia de `Select`: trigger compacto que
 * comunica el estado (todo / parcial con chips / ninguno), y un popover
 * con buscador, opción maestra "seleccionar todo" y scroll.
 * Compatible con dark mode mediante CSS variables.
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   label="Disponibilidad"
 *   value={sedes}
 *   onChange={setSedes}
 *   selectAllLabel="Todas las sedes"
 *   allSelectedLabel="Todas las sedes"
 *   options={[
 *     { value: '0001', label: 'Sede Principal', code: '0001' },
 *     { value: '0002', label: 'Sede Segunda',   code: '0002' },
 *   ]}
 *   required
 * />
 * ```
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(({
  label,
  labelAction,
  required = false,
  error,
  helperText,
  options = [],
  value,
  onChange,
  placeholder = 'Seleccionar…',
  selectAllLabel = 'Seleccionar todo',
  allSelectedLabel,
  size = 'medium',
  maxChips = 2,
  clearable = false,
  searchThreshold = 8,
  clearThreshold = 6,
  containerClassName = '',
  id,
  name,
  disabled = false,
}, ref) => {
  const fieldId = id || `multiselect-${Math.random().toString(36).substr(2, 9)}`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const masterRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    small: 'min-h-8 text-ui-sm py-0',
    medium: 'min-h-10 text-ui-base py-1.5',
    large: 'min-h-12 text-body py-2',
    auto: 'min-h-8 text-ui-sm py-0 2xl:min-h-10 2xl:text-ui-base 2xl:py-1.5',
  };
  const labelMargin = size === 'small' ? 'mb-1' : 'mb-1.5';

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectable = options.filter((o) => !o.disabled);
  const selected = options.filter((o) => selectedSet.has(o.value));
  const total = selectable.length;
  const count = selected.length;
  const allSelected = total > 0 && count === total;
  const noneSelected = count === 0;
  const showSearch = options.length > searchThreshold;

  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = !allSelected && !noneSelected;
  }, [allSelected, noneSelected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.code ? String(o.code).toLowerCase().includes(q) : false)
    );
  }, [options, query]);

  function toggle(val: string) {
    if (selectedSet.has(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  }
  function removeChip(val: string, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(value.filter((v) => v !== val));
  }
  function toggleAll() {
    onChange(allSelected ? [] : selectable.map((o) => o.value));
  }
  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  const visibleChips = selected.slice(0, maxChips);
  const hiddenCount = selected.length - visibleChips.length;
  const hasError = !!error;

  return (
    <div className={containerClassName} ref={rootRef}>
      {label && (
        <label
          htmlFor={fieldId}
          className={`block text-ui-sm font-medium ${labelMargin} text-tertiary`}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span>{label} {required && <span className="text-error">*</span>}</span>
              {labelAction}
            </span>
            {total > 0 && (
              <span className="text-caption text-muted tabular-nums font-normal">
                {count}/{total}
              </span>
            )}
          </span>
        </label>
      )}

      <div className="relative">
        <button
          ref={ref}
          id={fieldId}
          name={name}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={hasError}
          onClick={() => setOpen((v) => !v)}
          className={`w-full ${sizeClasses[size]} flex items-center gap-2 pl-3 pr-11 font-sans border rounded-lg transition-all duration-200 focus:outline-none bg-surface-0 cursor-pointer hover:border-strong focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
            hasError ? 'border-error' : 'border-strong'
          }`}
        >
          <span className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0 text-left">
            {noneSelected ? (
              <span className="text-muted">{placeholder}</span>
            ) : allSelected && allSelectedLabel ? (
              <span className="text-primary">{allSelectedLabel}</span>
            ) : (
              <>
                {visibleChips.map((o) => (
                  <span
                    key={o.value}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-surface-2 text-secondary text-ui-sm"
                  >
                    {o.label}
                    <span
                      role="button"
                      aria-label={`Quitar ${o.label}`}
                      onClick={(e) => removeChip(o.value, e)}
                      className="grid place-items-center w-4 h-4 rounded-full text-muted hover:text-primary"
                    >
                      <X size={12} />
                    </span>
                  </span>
                ))}
                {hiddenCount > 0 && (
                  <span className="text-ui-sm font-medium text-muted">+{hiddenCount}</span>
                )}
              </>
            )}
          </span>
          {clearable && !noneSelected && !disabled && (
            <span
              role="button"
              aria-label="Limpiar selección"
              onClick={clearAll}
              className="absolute right-9 top-1/2 -translate-y-1/2 grid place-items-center w-5 h-5 rounded-full text-muted hover:text-primary hover:bg-surface-2 transition-colors"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={18}
            className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-tertiary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div
            role="listbox"
            aria-multiselectable="true"
            className="absolute z-50 left-0 right-0 mt-1.5 bg-surface-0 border border-strong rounded-lg shadow-lg overflow-hidden"
          >
            {total > 1 && (
              <button
                type="button"
                onClick={toggleAll}
                className="w-full flex items-center gap-2.5 px-3 h-10 bg-surface-1 border-b border-strong hover:bg-surface-2 transition-colors cursor-pointer text-left"
              >
                <input ref={masterRef} type="checkbox" checked={allSelected} onChange={toggleAll} className="sr-only" />
                <span className={`grid place-items-center w-[18px] h-[18px] rounded-[5px] border transition-colors ${
                  allSelected ? 'bg-primary border-primary text-white' : !noneSelected ? 'bg-primary border-primary text-white' : 'border-strong bg-surface-0'
                }`}>
                  {allSelected ? <Check size={12} strokeWidth={3} /> : !noneSelected ? <Minus size={12} strokeWidth={3} /> : null}
                </span>
                <span className="text-ui-base font-medium text-primary">{selectAllLabel}</span>
              </button>
            )}

            {showSearch && (
              <div className="flex items-center gap-2 px-3 border-b border-strong text-tertiary">
                <Search size={15} />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar…"
                  className="flex-1 bg-transparent border-none outline-none py-2.5 text-ui-base text-primary placeholder:text-muted"
                />
              </div>
            )}

            <div className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-ui-sm text-muted">
                  Sin resultados para "{query}"
                </div>
              ) : (
                filtered.map((o) => {
                  const checked = selectedSet.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="option"
                      aria-selected={checked}
                      disabled={o.disabled}
                      onClick={() => toggle(o.value)}
                      className={`w-full flex items-center gap-2.5 px-3 h-10 transition-colors text-left cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                        checked ? 'bg-surface-2' : 'hover:bg-surface-1'
                      }`}
                    >
                      <span className={`grid place-items-center w-[18px] h-[18px] rounded-[5px] border transition-colors ${
                        checked ? 'bg-primary border-primary text-white' : 'border-strong bg-surface-0'
                      }`}>
                        {checked && <Check size={12} strokeWidth={3} />}
                      </span>
                      {o.code && (
                        <span className="text-ui-sm text-muted tabular-nums min-w-[34px]">{o.code}</span>
                      )}
                      <span className="text-ui-base text-primary">{o.label}</span>
                    </button>
                  );
                })
              )}
            </div>

            {options.length > clearThreshold && (
              <div className="flex items-center justify-end px-3 h-9 bg-surface-1 border-t border-strong">
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={noneSelected}
                  className="text-caption font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed cursor-pointer"
                >
                  Limpiar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1.5 text-caption text-error mt-2" id={`${fieldId}-error`}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {!error && helperText && (
        <div className="text-caption mt-2 text-tertiary" id={`${fieldId}-helper`}>
          {helperText}
        </div>
      )}
    </div>
  );
});

MultiSelect.displayName = 'MultiSelect';


